import {ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {DirectionsEntry} from '../../direction/directions.entry';
import {PlacesService, POST_CODE_REGEX} from '../places/places.service';
import {PlaceSource} from '../places/model/place-source';
import {TollsEntry} from '../../../pages/client/tasks/tasks-manager/tasks-queue/model/tolls/tolls.entry';
import {Alphabet} from '../../model/alphabet';
import {forkJoin, Observable, Subject} from 'rxjs';
import {RouteService} from '../route/route.service';
import {OlRouteService} from './ol-route.service';
import {moveItemInArray} from '@angular/cdk/drag-drop';
import {OlRouteLeg} from './ol-route-leg';
import {OlLegPoint} from './ol-leg-point';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Permissions} from '../../auth/permissions';
import {Module} from '../../model/profile';
import {CookieService} from 'ngx-cookie';
import {ROUTE_AVERAGE_SPEED} from '../../global/cookies.constants';
import OlMap from 'ol/Map';
import {Vector as VectorLayer} from 'ol/layer';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {SubscriptionUtils} from '../../commons/subscription.utils';
import {PlacesEntry} from '../places/model/places.entry';
import {VehiclesService} from '../../../pages/client/vehicles/vehicles.service';
import {
  VehicleParamsEntry
} from '../../../pages/client/vehicles/vehicles-list/vehicle-info/vehicle-parameters/model/vehicle-params.entry';
import {DEFAULT_TRUCK_FUEL_USAGE} from '../../../pages/client/tasks/pro-tasks-manager/form/pro-tasks-form.component';
import {RoutingParams} from '../route/model/routing-params';

export interface RoutePointData {
  place: string;
  position: number;
}

@Component({
  selector: 'app-ol-route',
  templateUrl: './ol-route.component.html',
  styleUrls: ['./ol-route.component.scss'],
})
export class OlRouteComponent implements OnInit, OnDestroy {

  @Input()
  public set setHeight(height: string) {
    let element = document.getElementById('route-window-resizable');
    if (element != null) {
      element.style.height = height;
    }
  }

  @Input()
  public small = false;

  public map: OlMap;
  public routeRequest: any;
  public activeRoute: string;
  public routePolyline: string;
  public routeLayer: VectorLayer;

  public displayedPoints: any[] = [];
  public calculatedRoute: DirectionsEntry = this.getEmpty();
  public avgCalculatedRoute: DirectionsEntry = this.getEmpty()
  public routeNotAvailable: boolean;
  public olRoute: Map<number, OlRouteLeg> = new Map<number, OlRouteLeg>();
  private routingOptions: RoutingParams;
  public lightRoute = false;
  public dragDisabled = false;

  private subSubject = new Subject<boolean>();

  private clickListenerCleanup: () => void;

  public alphabetList = Alphabet.ARRAY;

  public paramsOpened = false;

  public priceOpened = false;

  public routeAvoids = ['TOLLS', 'HIGHWAYS', 'FERRIES'];

  public avoids = [];

  public calcStrategies = ['FASTEST', 'BALANCED'];

  public olRouteForm: UntypedFormGroup;

  public hasHeavyTracking: boolean;

  public useAverageSpeed: boolean;

  public searchResults = new Map<number, PlacesEntry[]>();
  public dropDownIndex = -1;

  public tollPrice: TollsEntry = {totalCost: 0, countryTolls: []};
  private searchSubject: Subject<string> = new Subject();
  private focusedIdx = 1;
  private selectedItem: { result: PlacesEntry, idx: number };
  private keyword: string;
  private vehicleParams: VehicleParamsEntry = null;

  constructor(private placesService: PlacesService,
              public fb: UntypedFormBuilder,
              private ngZone: NgZone,
              private routeService: RouteService,
              private olRouteService: OlRouteService,
              private cd: ChangeDetectorRef,
              private renderer: Renderer2,
              private cookieService: CookieService,
              private vehiclesService: VehiclesService) {

    this.clickListenerCleanup = this.renderer.listen('window', 'click', (_e: Event) => {
      this.clearSearch();
    });

    this.setupOlRouteGroup();
  }

  private clearSearch() {
    this.searchResults.clear();
    for (let i = 0; i < this.displayedPoints.length; i++) {
      this.hideShowMore(i);
    }
  }

  public ngOnInit(): void {
    this.hasHeavyTracking = Permissions.hasModule(Module.HEAVY_TRACK);
    this.clearDisplayedPoints();
    window['olRouteRef'] = {component: this, zone: this.ngZone};

    this.olRouteService.getOlRouteDirections().pipe(takeUntil(this.subSubject)).subscribe(
      data => this.handleRoute(data)
    );

    this.olRouteService.removedWayPoints().pipe(takeUntil(this.subSubject)).subscribe(
      data => {
        this.remove(data[0], data[1])
      }
    );

    this.routeService.getRoutingOptions().pipe(takeUntil(this.subSubject)).subscribe(
      data => {
        this.routingOptions = data;
        if (data != null) {
          this.patchRoutingOptions();
        }
      }
    );

    this.olRouteService.getMapInit().pipe(takeUntil(this.subSubject)).subscribe(
      data => {
        this.map = data.map;
        this.routeLayer = data.layer;
      }
    )

    this.olRoute = this.olRouteService.getOlRoute();

    this.olRouteService.getRemovedPoints().pipe(takeUntil(this.subSubject)).subscribe(
      data => this.remove(data)
    );
    this.searchSubject.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntil(this.subSubject)
    ).subscribe((event) => this.handleDebounce(event));
    this.initAverageSpeed();
  }

  public switchRoute() {
    this.lightRoute = !this.lightRoute;
    this.setupOlRouteGroup();
    this.calculate();
  }

  public drop(event: any) {
    let previous = event.previousIndex;
    let current = event.currentIndex;
    moveItemInArray(this.displayedPoints, previous, current);
    this.moveItemsInMap(this.displayedPoints);
    this.calculate();
  }

  public handleRoute(data: any) {
    this.olRoute = data;
    this.clearDisplayedPoints();
    this.olRoute.forEach((value, key) => {
      if (key === 1) {
        if (value.origin) {
          this.displayedPoints[0] = value.origin
        }
        if (value.destination) {
          this.displayedPoints[1] = value.destination;
        }
      } else {
        this.displayedPoints.push(value.destination);
      }
    });
    this.calculate();
    if (!this.cd['destroyed']) {
      this.cd.detectChanges();
    }
  }

  private handleVehicle(): void {
    let vehicle = this.findVehiclePoint();
    if (vehicle) {
      this.loadVehicleParams(vehicle);
    } else {
      this.patchDefaultVehicleParams();
    }
  }

  private findVehiclePoint(): any {
    let routeLeg = [...this.olRoute].map(entry => entry[1])
      .find(routeLeg => routeLeg.origin?.vehicleId != null || routeLeg.destination?.vehicleId != null);
    if (routeLeg != null) {
      return routeLeg.origin?.vehicleId != null
        ? routeLeg.origin
        : routeLeg.destination;
    }
    return null;
  }

  private loadVehicleParams(vehicle: OlLegPoint): void {
    if (this.vehicleParams?.vehicleId !== vehicle.vehicleId) {
      this.vehiclesService.getVehicleParams(vehicle.vehicleId)
        .pipe(takeUntil(this.subSubject))
        .subscribe((data) => {
          this.vehicleParams = data;
          this.patchVehicleParams(vehicle.fuelUsage);
        });
    }
  }

  public remove(id: number, waypoint?: number): void {
    if (waypoint != null) {
      this.olRoute.get(id).waypoints.splice(waypoint, 1);
      this.calculate();
      return;
    }
    if (this.displayedPoints.length > 2) {
      this.displayedPoints.splice(id, 1);
      this.olRoute.delete(id);
      let keyIndex = id === 0 ? id + 1 : id;
      this.olRoute.forEach((value, key) => {
        if (key > keyIndex) {
          this.olRoute.set(key - 1, value);
        }
      });
      this.moveItemsInMap(this.displayedPoints);
      if (this.olRoute.size >= this.displayedPoints.length) {
        this.olRoute.delete(this.olRoute.size);
      }
      this.olRouteService.removeRouteMarker(id);
      this.calculate();
    } else {
      this.olRouteService.removeRouteMarker(id);
      this.displayedPoints[id].name = '';
    }
  }

  public addViaPoint(): void {
    this.displayedPoints.push(OlLegPoint.empty());
    this.olRoute.set(this.olRoute.size + 1, OlRouteLeg.empty());
  }

  public onChangeValue(value, id) {
    this.displayedPoints[id] = OlLegPoint.fromPoint(value.lat, value.lng, value.name);

    let viaPoint = this.displayedPoints[id];
    if (value.lat !== 0 && value.lng !== 0) {
      this.addRoutePoint(value, id);
      this.calculate(value);
    } else {
      this.placesService.placeDetails(value.placeId).pipe(takeUntil(this.subSubject)).subscribe(
        (data) => {
          viaPoint.lat = data.lat;
          viaPoint.lng = data.lng;
          this.calculate();
        }
      );
    }
  }

  public calculate(place?: string, idx?: number): void {
    this.handleVehicle();
    this.routeNotAvailable = false;
    this.dragDisabled = true;
    let canCalculate = true;
    this.olRoute.forEach(route => {
      if (!route.origin || !route.destination || !route.origin.lat || !route.destination.lat) {
        canCalculate = false;
      }
    });
    if (canCalculate) {
      this.calculateTruck();
      this.disableButton(false);
    } else {
      if (place) {
        this.placesService.putOlMarker({place: place, position: idx});
      }
      this.dragDisabled = false;
    }
  }

  private calculateTruck() {
    this.calculatedRoute = this.getEmpty();
    this.avgCalculatedRoute = this.getEmpty();
    this.tollPrice = {totalCost: 0, countryTolls: []};
    this.calculateAllRoutes().pipe(takeUntil(this.subSubject)).subscribe(
      (data) => {
        this.calculateRouteDetails();
        this.olRouteService.routeReturn(this.olRoute);
        this.setActiveRouteInfo(this.focusedIdx);
        this.checkIfRouteIsAvailable();
        this.cd.detectChanges();
        this.dragDisabled = false;
      });
  }

  private calculateAllRoutes(): Observable<any[]> {
    let requests = [];
    this.olRoute.forEach((value, key) => requests.push(this.getRoute(value, key)));
    return forkJoin(requests);
  }

  private getRoute(request, idx?) {
    let truckRequest = this.getTruckRequest(request);
    return new Promise(resolve => {
      this.routeService.altHereRoute(truckRequest).pipe(takeUntil(this.subSubject)).subscribe(
        data => {
          this.showRouteOnMap(data, idx);
          resolve(data);
        },
        err => {
          resolve(null);
          this.cd.markForCheck();
        }
      )
    });
  }

  private calculateRouteDetails() {
    this.calculatedRoute = this.getEmpty();
    this.avgCalculatedRoute = this.getEmpty();
    this.tollPrice = {totalCost: 0, countryTolls: []};
    this.olRoute.forEach((value, key) => {
      let dur = (value.distance / 71000 * 3600).toFixed(0);
      let fuelPrice = this.getFuelPrice(value.distance);
      let operatingCost = this.getTruckOperatingCost(value.duration);
      let avgOperatingCost = this.getTruckOperatingCost(+dur);
      this.calculatedRoute.legs.push({
        duration: value.duration,
        distance: value.distance,
        legs: [],
        idx: key,
        fuelPrice: fuelPrice,
        truckOperatingCost: operatingCost
      });
      this.calculatedRoute.duration += value.duration;
      this.calculatedRoute.distance += value.distance;
      this.calculatedRoute.fuelPrice += fuelPrice;
      this.calculatedRoute.truckOperatingCost += operatingCost;

      this.avgCalculatedRoute.legs.push({
        duration: +dur,
        distance: value.distance,
        legs: [],
        idx: key,
        fuelPrice: fuelPrice,
        truckOperatingCost: avgOperatingCost
      });
      this.avgCalculatedRoute.duration += +dur;
      this.avgCalculatedRoute.distance += value.distance;
      this.avgCalculatedRoute.fuelPrice += fuelPrice;
      this.avgCalculatedRoute.truckOperatingCost += avgOperatingCost;
      this.tollPrice.totalCost += value.tolls.totalCost;
      for (let countryToll of value.tolls.countryTolls) {
        this.tollPrice.countryTolls.push(countryToll);
      }
      this.tollPrice.countryTolls = this.mergeCountryTolls();
    });
  }

  private getEmpty(): DirectionsEntry {
    return {duration: 0, distance: 0, legs: [], fuelPrice: 0, truckOperatingCost: 0};
  }

  private mergeCountryTolls() {
    let helper = {};
    return this.tollPrice.countryTolls
      .reduce(function (countryTolls, countryToll) {
        let key = countryToll.country;
        if (!helper[key]) {
          helper[key] = Object.assign({}, countryToll);
          countryTolls.push(helper[key]);
        } else {
          helper[key].amountInTargetCurrency += countryToll.amountInTargetCurrency;
        }
        return countryTolls;
      }, []);
  }

  private moveItemsInMap(displayedPoints: any[]) {
    for (let i = 0; i < displayedPoints.length - 1; i++) {
      let key = i + 1;
      if (!this.compareMapLegs(displayedPoints[i], displayedPoints[i + 1], key)) {
        this.olRoute.get(key).origin = displayedPoints[i];
        this.olRoute.get(key).destination = displayedPoints[i + 1];
        this.olRoute.get(key).waypoints = [];
      }
    }
  }

  private showRouteOnMap(route: any, idx: number) {
    if (route != null) {
      let routeReturn = this.olRoute.get(idx);
      routeReturn.polyline = route.polyline;
      routeReturn.distance = +route.distance;
      routeReturn.duration = route.duration;
      routeReturn.tolls = route.tollsEntry;
    }
  }

  private compareMapLegs(origin: any, destination: any, key: number) {
    return this.olRoute.get(key).origin === origin && this.olRoute.get(key).destination === destination;
  }

  private clear() {
    this.clearDisplayedPoints();
    this.calculatedRoute = null;
    this.avgCalculatedRoute = null;
    this.olRouteService.routeReturn(null);
    this.olRouteService.olDirection(null);
    this.placesService.putMarker(null);
    this.olRoute = this.olRouteService.getOlRoute()
  }

  private clearDisplayedPoints() {
    this.displayedPoints = [
      OlLegPoint.empty(),
      OlLegPoint.empty()
    ];
  }

  public close(): void {
    this.clear();
  }

  private getTruckRequest(request: any) {
    return {
      origin: this.returnPoint(request.origin),
      destination: this.returnPoint(request.destination),
      waypoints: request.waypoints,
      vehicleType: this.lightRoute ? 'CAR' : 'TRUCK',
      strategy: this.olRouteForm.controls['strategy'].value,
      avoids: this.olRouteForm.controls['avoids'].value,
      weight: this.olRouteForm.controls['weight'].value,
      width: this.olRouteForm.controls['width'].value,
      height: this.olRouteForm.controls['height'].value
    };
  }

  private returnPoint(point: any) {
    return {lat: point.lat, lng: point.lng, address: point.name};
  }

  private addRoutePoint(value: any, id: number) {
    if (!this.olRoute || this.olRoute.size === 0) {
      this.olRoute = this.olRouteService.getOlRoute();
    }
    if (id === 0) {
      this.olRoute.get(1).origin = OlLegPoint.fromPoint(value.lat, value.lng, value.name);
    }
    if (id === 1) {
      this.olRoute.get(1).destination = OlLegPoint.fromPoint(value.lat, value.lng, value.name);
      this.setHigherOrigin(id);
    }
    if (id > 1) {
      let newLeg = OlRouteLeg.empty();
      newLeg.origin = this.olRoute.get(id - 1).destination;
      newLeg.destination = OlLegPoint.fromPoint(value.lat, value.lng, value.name);
      this.olRoute.set(id, newLeg);
      this.setHigherOrigin(id);
      this.moveItemsInMap(this.displayedPoints);
    }
  }

  private setHigherOrigin(id: number) {
    if (this.olRoute.size > id) {
      this.olRoute.get(id + 1).origin = this.olRoute.get(id).destination;
    }
  }

  private checkIfRouteIsAvailable() {
    this.olRoute.forEach((value) => {
      if (value.polyline[0] === 'route_calculation_failed') {
        this.routeNotAvailable = true;
      }
    })
  }

  public onKeydown($event, index: number) {
    switch ($event.key) {
      case 'Enter':
        this.handleEnter();
        return;
      case 'Escape':
        this.clearSearch();
        return;
      case 'ArrowDown':
        $event.preventDefault();
        $event.stopPropagation();
        this.handleDown(index);
        return;
      case 'ArrowUp':
        $event.preventDefault();
        $event.stopPropagation();
        this.handleUp(index);
        return;
      case 'Control':
      case 'v':
        return;
      default:
        this.searchSubject.next($event);
        return;
    }
  }

  private handleDebounce($event: any) {
    this.search($event);
  }

  private search($event: any) {
    if (this.keyword !== $event.target.value) {
      this.keyword = $event.target.value;
      let sources = [
        PlaceSource.NOMINATIM_COORDINATES,
        PlaceSource.VEHICLES,
        PlaceSource.DRIVERS,
        PlaceSource.POI];

      if (this.keyword.length > 3) {
        if (this.keyword.match(POST_CODE_REGEX)) {
          sources.push(PlaceSource.GEONAMES);
        } else {
          sources.push(PlaceSource.ARCGIS_ADDRESSES);
        }
      }
      let input = +$event.target.id.replace('via-', '');
      this.placesService.getPlaces(this.keyword, sources).pipe(takeUntil(this.subSubject)).subscribe(
        (data) => {
          this.searchResults.set(input, data);
          this.selectedItem = {result: this.searchResults.get(input)[0], idx: input};
          this.dropDownIndex = -1;
          this.showShowMore(input);
        }
      );
    }
  }

  public showMore(idx: number, event) {
    event.stopPropagation();
    let sources = [PlaceSource.GOOGLE_ADDRESS];
    this.placesService.getPlaces(this.keyword, sources).pipe(takeUntil(this.subSubject)).subscribe(
      (data) => {
        let previousResults = this.searchResults.get(idx);
        this.searchResults.set(idx, previousResults.concat(data));
        this.hideShowMore(idx);
      }
    );
  }

  private hideShowMore(idx: number) {
    let element = document.getElementById(`show-more-${idx}`);
    if (element != null) {
      element.classList.add('hidden');
    }
  }

  private showShowMore(idx: number) {
    setTimeout(() => document.getElementById(`show-more-${idx}`).classList.remove('hidden'), 500);
  }

  public setViaPoint(result: PlacesEntry, idx: number) {
    if (result.lat !== 0 && result.lng !== 0) {
      this.addViaPointInfo(result, idx);
    } else {
      this.placesService.placeDetails(result.placeId).pipe(takeUntil(this.subSubject)).subscribe(
        (data) => {
          this.addViaPointInfo(data, idx);
        }
      );
    }
  }

  private addViaPointInfo(result: PlacesEntry, idx: number) {
    let viaPoint = this.displayedPoints[+idx];
    viaPoint.lat = result.lat;
    viaPoint.lng = result.lng;
    viaPoint.name = result.name;
    this.searchResults.clear();
    this.addRoutePoint(viaPoint, +idx);
    this.calculate(viaPoint, idx);
    this.hideShowMore(idx);
  }

  private handleEnter(): void {
    if (this.selectedItem?.result) {
      this.setViaPoint(this.selectedItem.result, this.selectedItem.idx);
    }
  }

  private handleDown(index: number): void {
    this.cd.detach();
    let foundResults = this.searchResults.get(index);
    this.resolveDropdownIdx(foundResults, true);
    if (foundResults?.length > 0 && this.dropDownIndex < foundResults.length) {
      this.selectedItem = {result: foundResults[this.dropDownIndex], idx: index};
    } else {
      this.selectedItem = null;
    }
    this.cd.reattach();
  }

  private handleUp(index: number): void {
    this.cd.detach();
    let foundResults = this.searchResults.get(index);
    this.resolveDropdownIdx(foundResults, false);
    if (foundResults?.length > 0 && this.dropDownIndex >= 0) {
      this.selectedItem = {result: foundResults[this.dropDownIndex], idx: index};
    } else {
      this.selectedItem = null;
    }
    this.cd.reattach();
  }

  private resolveDropdownIdx(results: PlacesEntry[], down: boolean): void {
    if (results) {
      if (down) {
        this.dropDownIndex = results.length -1 > this.dropDownIndex ? ++this.dropDownIndex : this.dropDownIndex;
      } else {
        this.dropDownIndex = results.length > 0 && this.dropDownIndex > 0 ? --this.dropDownIndex : this.dropDownIndex;
      }
    } else {
      this.dropDownIndex = -1;
    }
  }

  public focusInput(id: number) {
    this.focusedIdx = id;
    this.olRouteService.focusInput(id);
    this.handlePasteEvent(id);
    this.setActiveRouteInfo(id);
  }

  private setActiveRouteInfo(id: number) {
    let route = this.olRoute.get(id);
    if (route != null) {
      this.routeRequest = route;
      this.activeRoute = route.uuid;
      if (route.polyline != null) {
        let routeLatLngs = [];
        route.polyline.forEach(polyline => {
          let latLngs = google.maps.geometry.encoding.decodePath(polyline);
          routeLatLngs = routeLatLngs.concat(latLngs);
        });
        this.routePolyline = google.maps.geometry.encoding.encodePath(routeLatLngs);
      }
    }
  }

  private handlePasteEvent(id: number) {
    let addressInput = document.getElementById(`via-${id}`);
    addressInput.addEventListener('paste', (event) => {
      let data = event.clipboardData.getData('text');
      if (data != null && data !== '') {
        setTimeout(() => event.preventDefault(), 5);
        let numberWithSpaceRegExp = new RegExp(/(\d)\s(?=\d)/);
        let latLng = data.split(/[, ]+/);
        let numberRegExp = /^[-+]?\d+\.\d+$/;
        if (numberRegExp.test(latLng[0]) && numberRegExp.test(latLng[1])) {
          this.onChangeValue({
            lat: latLng[0], lng: latLng[1], name: data
              .replace(numberWithSpaceRegExp, ',')
              .replace(/\s+/g, '')
          }, id);
        } else {
          this.hideShowMore(id);
          let postCodeRequest = data.match(POST_CODE_REGEX);
          let source = postCodeRequest ? [PlaceSource.GEONAMES] : [PlaceSource.ARCGIS_ADDRESSES];
          this.keyword = data;
          this.placesService.getPlaces(this.keyword, source).pipe(takeUntil(this.subSubject)).subscribe(
            (data) => {
              this.searchResults.set(id, data);
              this.selectedItem = {result: this.searchResults.get(id)[0], idx: id};
              this.dropDownIndex = -1;
            });
          if (postCodeRequest) {
            this.showShowMore(id)
          }
          this.disableButton(true);
          event.stopImmediatePropagation();
        }
      }
    });
  }

  public changeState() {
    this.paramsOpened = !this.paramsOpened;
  }

  public changePriceState() {
    this.priceOpened = !this.priceOpened;
  }

  public addOrRemoveAvoid(avoid: string, $event: any) {
    if ($event.target.checked) {
      this.avoids.push(avoid);
    } else {
      let index = this.avoids.indexOf(avoid);
      this.avoids.splice(index, 1);
    }
    this.olRouteForm.patchValue({avoids: this.avoids});
  }

  public setupOlRouteGroup() {
    this.olRouteForm = this.fb.group({
      weight: [this.lightRoute ? 3500 : 40000],
      height: [this.lightRoute ? 2.8 : 4],
      width: [this.lightRoute ? 2.0 : 2.5],
      avoids: [this.avoids],
      strategy: [this.calcStrategies[0]],
      averageFuelPrice: [this.routingOptions != null ? this.routingOptions.averageFuelPrice : 0],
      averageTruckRentPrice: [this.routingOptions != null ? this.routingOptions.averageTruckRentPrice : 0],
      fuelUsageFor100km: [DEFAULT_TRUCK_FUEL_USAGE]
    });
  }

  private patchRoutingOptions(): void {
    this.olRouteForm.patchValue({
      averageFuelPrice: this.routingOptions.averageFuelPrice,
      averageTruckRentPrice: this.routingOptions.averageTruckRentPrice
    });
  }

  private patchVehicleParams(fuelUsage: number): void {
    this.olRouteForm.patchValue({
      weight: this.vehicleParams.weight,
      height: this.vehicleParams.height,
      width: this.vehicleParams.width,
      fuelUsageFor100km: fuelUsage != null && fuelUsage !== 0
        ? fuelUsage
        : DEFAULT_TRUCK_FUEL_USAGE
    });
  }

  private patchDefaultVehicleParams(): void {
    if (this.vehicleParams != null) {
      this.olRouteForm.patchValue({
        weight: this.lightRoute ? 3500 : 40000,
        height: this.lightRoute ? 2.8 : 4,
        width: this.lightRoute ? 2.0 : 2.5,
        fuelUsageFor100km: DEFAULT_TRUCK_FUEL_USAGE
      });
      this.vehicleParams = null;
    }
  }

  private disableButton(state: boolean) {
    let button = <HTMLButtonElement>document.getElementById('calculate-button');
    if (button) {
      button.disabled = state;
    }
  }

  private initAverageSpeed() {
    let cookiesAverageSpeed = this.cookieService.get(ROUTE_AVERAGE_SPEED);
    this.useAverageSpeed = cookiesAverageSpeed == null
      ? false
      : cookiesAverageSpeed === 'true';
  }

  public changeAverageSpeedState(state: boolean) {
    this.useAverageSpeed = state;
    this.cookieService.put(ROUTE_AVERAGE_SPEED, JSON.stringify(this.useAverageSpeed));
  }

  private getFuelPrice(distance: number): number {
    let liters = (distance / 100000) * this.olRouteForm.value.fuelUsageFor100km;
    let price = liters * this.olRouteForm.value.averageFuelPrice;
    return +price.toFixed(2);
  }

  private getTruckOperatingCost(duration: number): number {
    let oneMinutePrice = this.olRouteForm.value.averageTruckRentPrice / 60;
    let durationInMinutes = duration / 60;
    let price = durationInMinutes * oneMinutePrice;
    return +price.toFixed(2);
  }

  public getTotalRoutePrice(): number {
    let operatingCost = this.useAverageSpeed
      ? this.avgCalculatedRoute.truckOperatingCost
      : this.calculatedRoute.truckOperatingCost;
    return this.tollPrice.totalCost + this.calculatedRoute.fuelPrice + operatingCost;
  }

  public getRouteLegs() {
    let sortedAvgRouteLegs = this.avgCalculatedRoute.legs.sort((a, b) => a.idx > b.idx ? 1 : -1);
    let sortedRouteLegs = this.calculatedRoute.legs.sort((a, b) => a.idx > b.idx ? 1 : -1);
    return this.useAverageSpeed
      ? sortedAvgRouteLegs
      : sortedRouteLegs;
  }

  public recalculate() {
    this.calculateTruck();
  }

  public ngOnDestroy(): void {
    this.clear();
    SubscriptionUtils.safeUnsubscribeSubject(this.subSubject);
    if (this.searchSubject != null) {
      this.searchSubject.unsubscribe();
    }
    window['olRouteRef'] = null;
    if (this.clickListenerCleanup) {
      this.clickListenerCleanup();
    }
  }

}
